import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Alert,
  Badge,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { isObject } from "lodash"
import { checkForMinusNumber, numberIsSame } from "utils/utils"

const DraftEditModal = ({ isOpen, toggle, draftData, onSubmit, loading }) => {
  const [error, setError] = useState("")
  const [balance, setBalance] = useState(0)

  // Get receipt information from draft data
  const getReceiptInfo = () => {
    if (!draftData?.provider_in) return null
    return draftData.provider_in
  }

  // Get parsed draft data
  const getParsedDraftData = () => {
    if (!draftData?.data) return {}
    try {
      return typeof draftData.data === "string"
        ? JSON.parse(draftData.data)
        : draftData.data
    } catch (e) {
      console.error("Error parsing draft data:", e)
      return {}
    }
  }

  // Calculate balance (available quantity - current out quantity)
  const calculateBalance = (availableQty, currentOutQty) => {
    const available = availableQty || 0
    const current = currentOutQty || 0
    return Math.max(0, available - current)
  }

  // Calculate UNFPA values like Add Distribution
  const calculateUNFPAValues = (outQuantity, receiptData) => {
    if (!outQuantity || !receiptData)
      return { unfpa_out_quantity: "", unfpa_total_value: "" }

    const qty = outQuantity
    const unfpa_quantity_receipt = receiptData?.unit_uom_quantity_per_unfpa || 1
    const ip_quantity = receiptData?.unit_umo_quantity_per_ip || 1

    let unfpa_quantity
    if (!numberIsSame(unfpa_quantity_receipt, ip_quantity)) {
      unfpa_quantity = qty / unfpa_quantity_receipt
    } else {
      unfpa_quantity = qty
    }

    const total_value =
      unfpa_quantity * (receiptData?.unit_price_per_unfpa || 0)

    return {
      unfpa_out_quantity: unfpa_quantity.toFixed(2),
      unfpa_total_value: total_value.toFixed(2),
    }
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      out_quantity: getParsedDraftData()?.out_quantity || "",
      distributor: getParsedDraftData()?.distributor || "",
      date: getParsedDraftData()?.date || "",
      distribution_reason: getParsedDraftData()?.distribution_reason || "",
    },
    validationSchema: Yup.object({
      out_quantity: Yup.number()
        .required("Quantity is required")
        .min(1, "Quantity must be greater than 0")
        .test(
          "balance-check",
          "Quantity exceeds available balance",
          function (value) {
            const receiptInfo = getReceiptInfo()
            if (!receiptInfo || !value) return true
            const availableQty = receiptInfo.in_qty_per_ip || 0
            return value <= availableQty
          }
        ),
      date: Yup.string().required("Date is required"),
    }),
    onSubmit: values => {
      try {
        const receiptInfo = getReceiptInfo()

        // Calculate UNFPA values using receipt data
        const calculated = calculateUNFPAValues(
          values.out_quantity,
          receiptInfo
        )

        // Prepare the updated data
        const currentData = getParsedDraftData()
        const updatedData = {
          ...currentData,
          out_quantity: values.out_quantity,
          distributor: values.distributor,
          date: values.date,
          distribution_reason: values.distribution_reason,
          unfpa_out_quantity: calculated.unfpa_out_quantity,
          unfpa_total_value: calculated.unfpa_total_value,
        }

        onSubmit(updatedData)
      } catch (err) {
        setError("Failed to update draft. Please try again.")
      }
    },
  })

  // Handle quantity change and update balance
  const handleQuantityChange = e => {
    if (checkForMinusNumber(e)) {
      e.target.value = 0
      return false
    }

    const quantity = e.target.value
    validation.handleChange(e)

    // Update balance
    const receiptInfo = getReceiptInfo()
    if (receiptInfo) {
      const newBalance = calculateBalance(receiptInfo.in_qty_per_ip, quantity)
      setBalance(newBalance)
    }
  }

  // Reset form when modal opens/closes and initialize balance
  useEffect(() => {
    if (isOpen && draftData) {
      validation.resetForm()
      setError("")

      // Initialize balance
      const receiptInfo = getReceiptInfo()
      const parsedData = getParsedDraftData()
      if (receiptInfo) {
        const initialBalance = calculateBalance(
          receiptInfo.in_qty_per_ip,
          parsedData.out_quantity
        )
        setBalance(initialBalance)
      }
    }
  }, [isOpen, draftData])

  const handleModalClose = () => {
    validation.resetForm()
    setError("")
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={handleModalClose} size="lg">
      <ModalHeader toggle={handleModalClose}>
        Edit Distribution Draft
      </ModalHeader>

      <Form
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
        }}
      >
        <ModalBody>
          {error && <Alert color="danger">{error}</Alert>}

          {/* Display read-only information */}
          <div className="mb-4 p-3 bg-light rounded">
            <h6 className="mb-3">Distribution Information</h6>
            <Row>
              <Col md={6}>
                <strong>Item:</strong> {getParsedDraftData()?.item3 || "N/A"}
              </Col>
              <Col md={6}>
                <strong>Province:</strong>{" "}
                {getParsedDraftData()?.item1 || "N/A"}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <strong>Batch Number:</strong>{" "}
                {getParsedDraftData()?.batch_number ||
                  getParsedDraftData()?.item4 ||
                  "N/A"}
              </Col>
              <Col md={6}>
                <strong>Distribution Type:</strong>{" "}
                {getParsedDraftData()?.distributed_type || "N/A"}
              </Col>
            </Row>
          </div>

          {/* Receipt Information and Balance */}
          {getReceiptInfo() && (
            <div className="mb-4 p-3 bg-info bg-opacity-10 rounded">
              <h6 className="mb-3 text-info">Receipt Information</h6>
              <Row>
                <Col md={4}>
                  <strong>Available Quantity:</strong>
                  <Badge color="primary" className="ms-2">
                    {getReceiptInfo().in_qty_per_ip || 0}
                  </Badge>
                </Col>
                <Col md={4}>
                  <strong>Current Out Qty:</strong>
                  <Badge color="secondary" className="ms-2">
                    {validation.values.out_quantity || 0}
                  </Badge>
                </Col>
                <Col md={4}>
                  <strong>Balance:</strong>
                  <Badge
                    color={balance >= 0 ? "success" : "danger"}
                    className="ms-2"
                  >
                    {balance}
                  </Badge>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={6}>
                  <strong>Expire Date:</strong>{" "}
                  {getReceiptInfo().expire_date || "N/A"}
                </Col>
                <Col md={6}>
                  <strong>Unit Price:</strong> $
                  {getReceiptInfo().unit_price_per_unfpa || 0}
                </Col>
              </Row>
            </div>
          )}

          {/* Editable fields */}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="out_quantity">Out Quantity *</Label>
                <Input
                  type="number"
                  name="out_quantity"
                  id="out_quantity"
                  placeholder="Enter quantity"
                  min="1"
                  max={getReceiptInfo()?.in_qty_per_ip || undefined}
                  value={validation.values.out_quantity}
                  onChange={handleQuantityChange}
                  onBlur={validation.handleBlur}
                  invalid={
                    validation.touched.out_quantity &&
                    validation.errors.out_quantity
                  }
                />
                {validation.touched.out_quantity &&
                  validation.errors.out_quantity && (
                    <FormFeedback>
                      {validation.errors.out_quantity}
                    </FormFeedback>
                  )}
              </FormGroup>
            </Col>

            <Col md={6}>
              <FormGroup>
                <Label for="date">Available Quantity *</Label>
                <Input type="number" name="date" id="date" disabled />
                {validation.touched.date && validation.errors.date && (
                  <FormFeedback>{validation.errors.date}</FormFeedback>
                )}
              </FormGroup>
            </Col>
          </Row>

          {/* Show calculated values */}
          {validation.values.out_quantity && getReceiptInfo() && (
            <div className="mt-3 p-3 bg-success bg-opacity-10 rounded">
              <h6 className="text-success">
                Calculated Values (Auto-computed)
              </h6>
              <Row>
                <Col md={6}>
                  <strong>UNFPA Out Quantity:</strong>
                  <Badge color="success" className="ms-2">
                    {
                      calculateUNFPAValues(
                        validation.values.out_quantity,
                        getReceiptInfo()
                      ).unfpa_out_quantity
                    }
                  </Badge>
                </Col>
                <Col md={6}>
                  <strong>UNFPA Total Value:</strong>
                  <Badge color="success" className="ms-2">
                    $
                    {
                      calculateUNFPAValues(
                        validation.values.out_quantity,
                        getReceiptInfo()
                      ).unfpa_total_value
                    }
                  </Badge>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            color="secondary"
            onClick={handleModalClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            type="submit"
            disabled={loading || balance < 0 || !validation.values.out_quantity}
          >
            {loading ? "Updating..." : "Update Draft"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  )
}

export default DraftEditModal
